import { RowOrigin } from './source-key.model'

/**
 * Typed mirror of the Flex Positions row-detail API contract
 * (GET /flex-positions/row-detail/{source}/{rowId}, backend ticket PVAL-26156).
 *
 * Copied verbatim from that contract. Four guarantees shape everything downstream and are worth
 * stating here rather than re-deriving at each call site:
 *  - An unknown (source, rowId) under a valid session is a 200 with `data: []`, never a 404, so an
 *    empty array is a legitimate answer and not an error path.
 *  - Null values are omitted server-side. A field that IS present therefore carries a real scalar
 *    — though Enfusion does send the literal *string* `'null'` for some absent fields, which is a
 *    value the UI has to recognise (see `isEmptyDetailValue`).
 *  - `value` is never reformatted server-side: it is a native JSON scalar. Enfusion's
 *    `{ value, currency }` envelopes are unwrapped to `value.value` upstream, so the currency is
 *    already gone by the time it reaches here — expect bare numbers, never `Money`.
 *  - Entries arrive sorted by (columnGroup, columnName), which is the table's default order.
 */

/** One entry as returned by GET /flex-positions/row-detail/{source}/{rowId}. */
export interface RowDetailEntry {
  /**
   * Column-source group key. Usually a `ColumnGroupKey` member, but the backend can also emit
   * `'UNKNOWN'` or `'aggregateInformation'` — neither of which is one (a known backend gap,
   * deliberately deferred there), so this is typed as a plain string and resolved to a display
   * label by `resolveGroupLabel`.
   */
  columnGroup: string
  /** Display label, already resolved server-side — render verbatim, never re-derive. */
  columnName: string
  value: string | number | boolean
}

export interface RowDetailResponse {
  data: RowDetailEntry[]
}

/**
 * The backend `FlexPositionSource` enum name used as the endpoint's `{source}` path segment.
 *
 * Deliberately narrower than {@link RpsSource}: flex-positions stores ENFUSION and PROMETHEUS rows
 * only, so `'CW'` is not expressible here. That makes "never call the endpoint for a Clearwater
 * row" a compile-time property of the service seam rather than a runtime check every caller has to
 * remember — see {@link rowDetailSourceOf}.
 */
export type RowDetailSource = 'ENFUSION' | 'PROMETHEUS'

/**
 * Map a row's origin onto the endpoint's `{source}` path segment, or `null` when that origin has no
 * row-detail endpoint at all.
 *
 * `null` today means exactly one thing: a Clearwater row. There is no CW row-detail endpoint (the
 * flex-positions store holds ENFUSION/PROMETHEUS only), so the UI shows an inline
 * not-available notice instead of issuing a request — see the TODO on `PositionDetailComponent`.
 */
export const rowDetailSourceOf = (
  origin: RowOrigin,
): RowDetailSource | null => {
  switch (origin) {
    case 'api':
      return 'ENFUSION'
    case 'cust':
    case 'trad':
      return 'PROMETHEUS'
    case 'cw':
      return null
  }
}

/** One rendered table row: the transposed Column Group / Column Name / Value triple. */
export interface DetailField {
  /** Resolved display label for `columnGroup`, e.g. `'Default'`. */
  group: string
  /** `columnName`, verbatim. */
  name: string
  /** `value`, formatted for display. */
  value: string
  /** Whether this field counts as empty — drives the "Hide empty fields" toggle. */
  empty: boolean
  /**
   * Whether `value` was rendered as a number: it went through the view model's number formatting
   * (a catalog `NUM`, or an un-catalogued real JSON number) and came out finite rather than `—`.
   * Drives the Excel export's cell type — `true` writes a Number cell, `false` a Text cell, so an
   * identifier, a numeric-looking string or a CUSIP is never re-typed by Excel.
   */
  numeric: boolean
}
